import { useState } from "react";
import { Sparkles, Loader2, Crown, TrendingUp, TrendingDown, RefreshCw, Check, Bookmark, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { LOTTERIES, type LotterySlug } from "@/hooks/use-lottery-selection";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { CheckScopeDialog } from "@/components/app/CheckScopeDialog";
import { useAiQuota } from "@/hooks/use-ai-quota";
import type { SavedGameScope } from "@/lib/check-scope";

interface Analysis {
  strengths: string[];
  weaknesses: string[];
  historicalComparison: string;
  suggestion: { description: string; swapOut?: number[]; swapIn?: number[] };
}

interface Props {
  numbers: number[];
  lotterySlug: LotterySlug;
  trigger?: React.ReactNode;
}

export function AnalyzeGameDialog({ numbers, lotterySlug, trigger }: Props) {
  const { has } = usePlanLimits();
  const { user } = useAuth();
  const { quota, refresh: refreshQuota } = useAiQuota();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Analysis | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<number[] | null>(null);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const isPremium = has("premium");
  const lotteryName = LOTTERIES.find((l) => l.slug === lotterySlug)?.name;

  const canApply =
    !!data?.suggestion?.swapOut?.length &&
    !!data?.suggestion?.swapIn?.length &&
    data.suggestion.swapOut.length === data.suggestion.swapIn.length;

  const applySuggestion = () => {
    if (!data?.suggestion?.swapOut || !data?.suggestion?.swapIn) return;
    const out = new Set(data.suggestion.swapOut);
    const next = numbers.filter((n) => !out.has(n)).concat(data.suggestion.swapIn);
    setApplied(Array.from(new Set(next)).sort((a, b) => a - b));
  };

  const saveAdjusted = async (scopeInfo: { scope: SavedGameScope; from: number | null; until: number | null; specialScheduleId?: string | null }) => {
    if (!user || !applied) return;
    setSaving(true);
    try {
      const { data: lot } = await supabase.from("lotteries").select("id").eq("slug", lotterySlug).maybeSingle();
      if (!lot) throw new Error("Loteria não encontrada.");
      const { error: insErr } = await supabase.from("saved_games").insert({
        user_id: user.id,
        lottery_id: lot.id,
        numbers: applied,
        label: "Ajustado pela IA",
        cost: 0,
        check_scope: scopeInfo.scope,
        check_from_contest: scopeInfo.from,
        check_until_contest: scopeInfo.until,
        special_schedule_id: scopeInfo.specialScheduleId ?? null,
      });
      if (insErr) throw insErr;
      toast({ title: "Jogo ajustado salvo", description: "Veja em Meus jogos." });
      setApplied(null);
      setOpen(false);
    } catch (e) {
      toast({ title: "Erro ao salvar", description: e instanceof Error ? e.message : "Erro inesperado.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    setApplied(null);
    try {
      const { data: resp, error: invokeError } = await supabase.functions.invoke("ai-analyze-game", {
        body: { lotterySlug, numbers },
      });
      if (invokeError) throw new Error(invokeError.message || "Erro ao analisar com IA.");
      if ((resp as { error?: string })?.error) throw new Error((resp as { error: string }).error);
      setData((resp as { analysis: Analysis }).analysis);
      refreshQuota();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro inesperado.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setApplied(null); if (o && !data && isPremium) run(); }}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm" className="h-7 text-primary hover:bg-primary/10">
            <Sparkles className="mr-1 h-3.5 w-3.5" /> Analisar com IA
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-light">
            <Sparkles className="h-5 w-5 text-primary" /> Análise IA
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-[10px] uppercase text-primary">
              <Crown className="mr-1 h-3 w-3" /> Premium
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Diagnóstico estatístico do jogo {numbers.slice().sort((a, b) => a - b).map((n) => String(n).padStart(2, "0")).join(" · ")} comparado aos últimos 100 sorteios.
          </DialogDescription>
          {isPremium && quota && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Plano <strong className="text-foreground">{quota.plan}</strong> · {Math.max(0, quota.daily_limit - quota.daily_used)}/{quota.daily_limit} análises restantes hoje
              {" · "}{Math.max(0, quota.monthly_limit - quota.monthly_used)}/{quota.monthly_limit} no mês. Repetições do mesmo jogo usam cache e não consomem quota.
            </p>
          )}
        </DialogHeader>

        {!isPremium ? (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm">
            Exclusivo Premium. <a href="/planos" className="text-primary underline">Conheça o Premium</a>.
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Analisando jogo…
          </div>
        ) : error ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
            <Button variant="outline" size="sm" onClick={run}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Tentar novamente
            </Button>
          </div>
        ) : data ? (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1 text-sm">
            {data.strengths?.length > 0 && (
              <div>
                <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-lt-teal">
                  <TrendingUp className="h-3.5 w-3.5" /> Forças
                </h4>
                <ul className="space-y-1 text-foreground">
                  {data.strengths.map((s, i) => <li key={i} className="leading-relaxed">• {s}</li>)}
                </ul>
              </div>
            )}
            {data.weaknesses?.length > 0 && (
              <div>
                <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-destructive">
                  <TrendingDown className="h-3.5 w-3.5" /> Pontos a melhorar
                </h4>
                <ul className="space-y-1 text-foreground">
                  {data.weaknesses.map((s, i) => <li key={i} className="leading-relaxed">• {s}</li>)}
                </ul>
              </div>
            )}
            {data.historicalComparison && (
              <div>
                <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Comparação histórica
                </h4>
                <p className="leading-relaxed text-foreground">{data.historicalComparison}</p>
              </div>
            )}
            {data.suggestion?.description && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                <h4 className="mb-1.5 text-xs font-medium uppercase tracking-wider text-primary">Sugestão de ajuste</h4>
                <p className="leading-relaxed text-foreground">{data.suggestion.description}</p>
                {data.suggestion.swapOut?.length && data.suggestion.swapIn?.length ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Trocar {data.suggestion.swapOut.join(", ")} → {data.suggestion.swapIn.join(", ")}
                  </p>
                ) : null}
                {canApply && !applied && (
                  <Button size="sm" className="mt-3" onClick={applySuggestion}>
                    <Check className="mr-1.5 h-3.5 w-3.5" /> Acatar sugestão
                  </Button>
                )}
                {applied && (
                  <div className="mt-3 space-y-2 rounded-md border border-primary/40 bg-background p-3">
                    <p className="text-[11px] uppercase tracking-wider text-primary">Jogo ajustado</p>
                    <div className="flex flex-wrap gap-1.5">
                      {applied.map((n) => (
                        <span key={n} className="flex h-7 w-7 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-xs font-medium text-primary">
                          {String(n).padStart(2, "0")}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" onClick={() => setScopeOpen(true)} disabled={saving}>
                        <Bookmark className="mr-1.5 h-3.5 w-3.5" /> Salvar jogo ajustado
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setApplied(null)} disabled={saving}>
                        <X className="mr-1.5 h-3.5 w-3.5" /> Descartar ajuste
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
      <CheckScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        lotterySlug={lotterySlug}
        lotteryName={lotteryName}
        onConfirm={async ({ scope, from, until, specialScheduleId }) => {
          await saveAdjusted({ scope, from, until, specialScheduleId });
        }}
      />
    </Dialog>
  );
}